/**
 * Batch-load item ratings for a submission owner (admin answer preview).
 *
 * @module useSubmissionItemRatings
 */

import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionItemRatings } from '@/lib/submissionsApi';
import type { RatingDetail } from '@/lib/ratingsApi';

export const SUBMISSION_ITEM_RATINGS_QUERY_KEY = ['submissionItemRatings'] as const;

/**
 * Fetch the learner's ratings for the given answer ids within a session/module.
 */
export function useSubmissionItemRatings(
  sessionId: string | undefined,
  module: string,
  targetIds: string[],
  enabled: boolean = true,
) {
  const sortedKey = [...targetIds].sort();
  const { data, isLoading, isError, refetch } = useQuery<RatingDetail[]>({
    queryKey: [...SUBMISSION_ITEM_RATINGS_QUERY_KEY, sessionId, module, sortedKey],
    queryFn: () => fetchSubmissionItemRatings(sessionId!, module, targetIds),
    enabled: enabled && Boolean(sessionId) && targetIds.length > 0,
    staleTime: 0,
  });

  const byKey = new Map<string, RatingDetail>();
  (data ?? []).forEach((rating) => {
    byKey.set(`${rating.targetType}:${rating.targetId}`, rating);
  });

  return { ratings: data ?? [], byKey, isLoading, isError, refetch };
}
